import { useState, useEffect } from "react";
import { ArrowRight, MapPin, Mail } from "lucide-react";
import api from "@/lib/api";
import InquiryForm from "@/components/InquiryForm";

const Princeton = () => {
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    api.getPrograms({ location: "princeton" }).then(r => setPrograms(r.data)).catch(() => {});
  }, []);

  return (
    <div className="pt-16" data-testid="princeton-page">
      {/* Hero */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://customer-assets-agu9un31.emergentagent.net/job_squash-nexus/artifacts/yv4t65hz_image.jpeg"
            alt="" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-[#101214]/55"></div>
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-[#0D6B56]" />
              <span className="overline">Princeton, New Jersey</span>
            </div>
            <h1 className="font-editorial text-4xl sm:text-5xl font-medium text-[#F2EDE4] mb-4">Princeton</h1>
            <p className="text-sm text-[#F2EDE4]/70 leading-relaxed mb-8 max-w-lg">
              NJSC — 11 Princess Road, Lawrence Township, NJ. Membership-based club with 24/7 court access.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="https://njsc.clubautomation.com" target="_blank" rel="noopener noreferrer" className="btn-primary text-xs" data-testid="princ-book-court">
                Book a Court <ArrowRight className="w-3.5 h-3.5" />
              </a>
              <a href="https://njsc.clubautomation.com" target="_blank" rel="noopener noreferrer" className="btn-secondary text-xs" data-testid="princ-member-login">
                Member Login
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Memberships */}
      <section className="section" style={{ background: "var(--lrg-surface)" }}>
        <div className="container-custom">
          <div className="overline mb-4">Memberships</div>
          <h2 className="font-editorial text-3xl font-medium text-[#F2EDE4] mb-10">NJSC Princeton</h2>
          <div className="lrg-card max-w-3xl">
            <h3 className="font-display text-lg font-semibold text-[#F2EDE4] mb-2">11 Princess Road, Lawrence Township</h3>
            <p className="text-[11px] text-[#6B6E71] mb-6 leading-[1.8]">4 courts. Key Club — 24/7 member access. Peak hours: M-F 4:30-7:30 PM, Sat 10 AM-1 PM ($20 surcharge for limited members).</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { type: "Junior", price: "$85/mo" },
                { type: "Adult Limited", price: "$115/mo" },
                { type: "Adult Unlimited", price: "$155/mo" },
                { type: "Family (4)", price: "$205/mo" },
              ].map((m, i) => (
                <div key={m.type} className="text-center py-4 bg-[#090B0C] border border-[#1D1F21]">
                  <p className="text-[9px] text-[#6B6E71] uppercase tracking-[0.15em] mb-2">{m.type}</p>
                  <p className="font-editorial text-xl text-[#0D6B56]">{m.price}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="section" style={{ background: "var(--lrg-bg)" }}>
        <div className="container-custom">
          <div className="overline mb-4">Programs at Princeton</div>
          <h2 className="font-editorial text-3xl font-medium text-[#F2EDE4] mb-10">Training Programs</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {programs.map((p, i) => (
              <div key={p.id} className="lrg-card" data-testid={`princ-program-${i}`}>
                <span className="text-[9px] font-medium text-[#0D6B56] tracking-[0.15em] uppercase">{p.category}</span>
                <h3 className="font-display text-base font-semibold text-[#F2EDE4] mt-3 mb-2">{p.name}</h3>
                <p className="text-[11px] text-[#3A3D40] leading-[1.8] mb-3">{p.schedule}</p>
                <p className="text-xs font-medium text-[#0D6B56]">{p.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="section" style={{ background: "var(--lrg-surface)" }} data-testid="princ-inquiry-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2">
              <div className="overline mb-4">Inquire</div>
              <h2 className="font-editorial text-3xl font-medium text-[#F2EDE4] mb-4">Lessons, Clinics & Match Play</h2>
              <p className="text-xs text-[#6B6E71] leading-[1.9] mb-8">Contact our manager to arrange private lessons, clinics, and match play sessions at NJSC Princeton. All communications by email.</p>
              <a href="mailto:Njsquashclub@gmail.com" className="flex items-center gap-2 text-xs text-[#6B6E71] hover:text-[#0D6B56] transition-colors" data-testid="princ-contact-email">
                <Mail className="w-3.5 h-3.5" /> Njsquashclub@gmail.com
              </a>
            </div>
            <div className="lg:col-span-3 lrg-card" style={{ background: "var(--lrg-bg)" }}>
              <InquiryForm location="princeton" testId="princ-inquiry" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Princeton;
